import { formatDate } from 'date-fns/format';
import { useEffect, useState } from 'react';
import useUpdateBillingDetailsMutation from '@/api/mutations/useUpdateBillingDetailsMutation';
import useUpdateSubscriptionMutation from '@/api/mutations/useUpdateSubscriptionMutation';
import useGetBillingDetailsQuery from '@/api/queries/useGetBillingDetailsQuery';
import useGetBillingInfoQuery from '@/api/queries/useGetBillingInfoQuery';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import {
  BillingAddress,
  BillingDetails,
  BillingInfo,
  formatSubscriptionStatus,
  PlanCode,
  SubscriptionUpdate,
} from '@/models/Billing';
import { formatPaymentMethodBrand, formatPaymentMethodExpiry } from '@/models/PaymentMethod';
import { Store } from '@/models/Store';
import StoreBillingControlsPanel from '@/modules/admin/components/StoreBillingControlsPanel';
import StoreBillingPaymentDetailsPanel from '@/modules/admin/components/StoreBillingPaymentDetailsPanel';
import BillingDetailsForm, {
  BillingDetailsFormData,
} from '@/modules/billing/components/BillingDetailsForm';
import { formatPlanCode } from '@/modules/billing/models/Plan';
import { getCountryName } from '@/utils/locale/getCountryName';
import toast from 'react-hot-toast';

interface StoreBillingPanelProps {
  store: Store;
}

const sectionClassName = 'rounded-2xl border border-slate-200 bg-white p-6 lg:p-8';

const sectionTitleClassName = 'text-lg font-semibold text-slate-900';

const toBillingAddress = (values: BillingDetailsFormData, addressId?: string): BillingAddress => ({
  id: addressId,
  building: values.building.trim(),
  street1: values.street1.trim(),
  street2: values.street2.trim(),
  postalCode: values.postalCode.trim(),
  region: values.region.trim(),
  city: values.city.trim(),
  countryCode: values.countryCode,
});

const StoreBillingPanel = ({ store }: StoreBillingPanelProps) => {
  const billingCustomer = { storeId: store.id };
  const {
    billingInfo,
    isLoading: billingInfoLoading,
    error: billingInfoError,
  } = useGetBillingInfoQuery(billingCustomer);
  const {
    billingDetails,
    isLoading: billingDetailsLoading,
    error: billingDetailsError,
  } = useGetBillingDetailsQuery(billingCustomer);
  const updateBillingDetails = useUpdateBillingDetailsMutation(billingCustomer);
  const updateSubscription = useUpdateSubscriptionMutation();
  const [billingEnabled, setBillingEnabled] = useState<boolean>(false);
  const [plan, setPlan] = useState<PlanCode>('paid');
  const [startedAt, setStartedAt] = useState<Date | null>(store.createdAt);
  const [latestBillingInfo, setLatestBillingInfo] = useState<BillingInfo | null>(null);

  useEffect(() => {
    const nextBillingInfo = latestBillingInfo ?? billingInfo;
    if (!nextBillingInfo) {
      return;
    }

    const nextPlan = nextBillingInfo.plan?.code ?? 'free';
    const nextStartedAt = nextBillingInfo.subscription?.startedAt;

    setBillingEnabled(nextPlan === 'paid');
    setPlan(nextPlan);
    setStartedAt(nextStartedAt ?? store.createdAt);
  }, [billingInfo, latestBillingInfo, store.createdAt]);

  const handleSave = async () => {
    const nextPlan: PlanCode = billingEnabled ? plan : 'free';
    const subscriptionUpdate: SubscriptionUpdate =
      nextPlan === 'paid'
        ? {
            customer: { storeId: store.id },
            plan: 'paid',
            billingCycle: 'monthly',
            startedAt: startedAt ?? undefined,
          }
        : {
            customer: { storeId: store.id },
            plan: 'free',
          };

    try {
      const nextBillingInfo = await updateSubscription.mutateAsync(subscriptionUpdate);

      setLatestBillingInfo(nextBillingInfo);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Billing updated for store "${store.name}".`}
          visible={t.visible}
        />
      ));
    } catch (error) {
      console.error('Failed to update store billing:', error);
    }
  };

  const handleBillingDetailsSave = async (values: BillingDetailsFormData) => {
    const payload: BillingDetails = {
      companyName: values.companyName.trim(),
      billingEmail: values.billingEmail.trim(),
      taxId: values.taxId.trim() || null,
      address: toBillingAddress(values, billingDetails?.address?.id),
    };

    try {
      await updateBillingDetails.mutateAsync(payload);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Billing details updated for store "${store.name}".`}
          visible={t.visible}
        />
      ));
    } catch (error) {
      console.error('Failed to update billing details:', error);
    }
  };

  const currentBillingInfo = latestBillingInfo ?? billingInfo ?? null;
  const currentPlan = currentBillingInfo?.plan?.code;
  const currentStatus = currentBillingInfo?.subscription?.status;
  const currentStartedAt = currentBillingInfo?.subscription?.startedAt;
  const currentBillingCycle = currentBillingInfo?.subscription?.billingCycle;
  const currentPlanLabel = currentPlan ? formatPlanCode(currentPlan) : 'Not loaded yet';
  const currentBillingEnabled =
    currentBillingInfo?.subscription?.isActive ?? (currentPlan === 'paid' ? true : null);
  const billingCountryCode =
    billingDetails?.address?.countryCode ??
    currentBillingInfo?.billingDetails?.address?.countryCode;
  const billingCountry = billingCountryCode ? getCountryName(billingCountryCode) : null;
  const paymentMethod = currentBillingInfo?.defaultPaymentMethod;
  const paymentMethodCard = paymentMethod?.card;
  const paymentMethodLabel = paymentMethodCard
    ? `${formatPaymentMethodBrand(paymentMethodCard.brand)} •••• ${paymentMethodCard.last4}`
    : null;
  const paymentMethodExpiry = paymentMethodCard
    ? formatPaymentMethodExpiry(paymentMethodCard.expMonth, paymentMethodCard.expYear)
    : null;
  const canEnableBilling = Boolean(billingCountryCode);

  const handleBillingEnabledChange = (nextValue: boolean) => {
    if (nextValue && !canEnableBilling) {
      return;
    }

    if (nextValue && plan === 'free') {
      setPlan('paid');
    }

    setBillingEnabled(nextValue);
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className={sectionClassName}>
        <div className="mb-6 border-b border-slate-200 pb-4">
          <div className={sectionTitleClassName}>Billing target</div>
        </div>
        <div className="mt-4 space-y-3 text-sm text-slate-700">
          <div>
            <div className="mb-1 font-medium text-slate-900">Store</div>
            <div>{store.name}</div>
          </div>
          <div>
            <div className="mb-1 font-medium text-slate-900">Contact email</div>
            <div>{store.email || 'Not provided'}</div>
          </div>
          <div>
            <div className="mb-1 font-medium text-slate-900">Retailer</div>
            <div>{store.retailer?.name || 'Not available'}</div>
          </div>
        </div>
      </div>

      <div className={sectionClassName}>
        <div className="mb-6 border-b border-slate-200 pb-4">
          <div className={sectionTitleClassName}>Billing summary</div>
        </div>
        {billingInfoLoading ? (
          <div className="flex justify-center py-12">
            <SpinnerIcon />
          </div>
        ) : billingInfoError ? (
          <div className="mt-4 rounded-md bg-red-50 p-4 text-sm text-red-700">
            Failed to load billing information.
          </div>
        ) : (
          <div className="mt-4 space-y-3 text-sm text-slate-700">
            <div>
              <div className="mb-1 font-medium text-slate-900">Billing enabled</div>
              <div>
                {currentBillingEnabled === null
                  ? 'Not loaded yet'
                  : currentBillingEnabled
                    ? 'Enabled'
                    : 'Disabled'}
              </div>
            </div>
            <div>
              <div className="mb-1 font-medium text-slate-900">Plan</div>
              <div>{currentPlanLabel}</div>
            </div>
            <div>
              <div className="mb-1 font-medium text-slate-900">Subscription status</div>
              <div>{formatSubscriptionStatus(currentStatus)}</div>
            </div>
            <div>
              <div className="mb-1 font-medium text-slate-900">Billing cycle</div>
              <div>{currentBillingCycle ? 'Monthly' : 'Not loaded yet'}</div>
            </div>
            <div>
              <div className="mb-1 font-medium text-slate-900">Started at</div>
              <div>
                {currentStartedAt ? formatDate(currentStartedAt, 'd MMM yyyy') : 'Not loaded yet'}
              </div>
            </div>
          </div>
        )}
      </div>

      <StoreBillingPaymentDetailsPanel
        isLoading={billingInfoLoading}
        error={billingInfoError}
        paymentMethod={paymentMethod}
        paymentMethodLabel={paymentMethodLabel}
        paymentMethodExpiry={paymentMethodExpiry}
      />

      <StoreBillingControlsPanel
        billing={{
          enabled: billingEnabled,
          canEnable: canEnableBilling,
          plan,
          startedAt,
          country: billingCountry,
          countryCode: billingCountryCode,
          currencyCode: currentBillingInfo?.currencyCode,
        }}
        loading={updateSubscription.isPending}
        onBillingEnabledChange={handleBillingEnabledChange}
        onPlanChange={setPlan}
        onStartedAtChange={setStartedAt}
        onSave={handleSave}
      />

      <div className={`lg:col-span-2 ${sectionClassName}`}>
        <div className="mb-6 border-b border-slate-200 pb-4">
          <div className={sectionTitleClassName}>Billing details</div>
        </div>

        <div>
          {billingDetailsLoading ? (
            <div className="flex justify-center py-12">
              <SpinnerIcon />
            </div>
          ) : billingDetailsError ? (
            <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
              Failed to load billing details.
            </div>
          ) : (
            <BillingDetailsForm
              billingDetails={billingDetails}
              loading={updateBillingDetails.isPending}
              onSubmit={handleBillingDetailsSave}
              submitLabel="Save Billing Details"
              embedded
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreBillingPanel;
